import type { FormikErrors, FormikTouched } from "formik";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { formatEwcDisplayCode } from "@/components/Forms/EwcCodeInput";
import { RequiredFieldMarker } from "@/components/Forms/RequiredFieldMarker";
import { RegLookupSelect, type RegLookupSelectItem } from "@/components/Forms/RegLookupSelect";
import type { ISiteOption } from "@/customTypings/customer/Customer";
import type { WasteMovementDirection } from "@/customTypings/wasteMovement/WasteMovement";
import { shouldShowFormFieldError } from "@/utils";
import type { WasteMovementFormValues } from "../model/formValues";
import type { WasteMovementReferenceLookups } from "../model/referenceLookups";
import type { WasteMovementCarrierLookups } from "../react/useWasteMovementCarrierLookups";
import {
    MEANS_OF_TRANSPORT_OPTIONS,
    MEANS_OF_TRANSPORT_ROAD,
    NO_REGISTRATION_REASON_OPTIONS,
    applyHaulierPrefill,
    applyVehiclePrefill,
} from "../core/wasteMovementReceiptCapture";
import { resolveLondonInstant } from "../core/wasteMovementReceiptTime";
import {
    effectiveEwcClassification,
    storedHazardousPopsState,
    type EffectiveEwcClassification,
    type HazardousPopsErrors,
    type StoredHazardousPopsState,
} from "../core/wasteMovementHazardousPops";
import { HazardousPopsSections } from "./HazardousPopsSections";
import { BrokerDealerSection } from "./BrokerDealerSection";
import type { BrokerDealerErrors } from "../core/wasteMovementBrokerDealer";
import "./WasteMovementFormGrid.css";

type Props = {
    movementDirection: WasteMovementDirection;
    isNew: boolean;
    sites: ISiteOption[];
    showSiteField: boolean;
    values: WasteMovementFormValues;
    errors: FormikErrors<WasteMovementFormValues>;
    touched: FormikTouched<WasteMovementFormValues>;
    submitCount: number;
    liveValidationAfterSubmit: boolean;
    setFieldValue: (
        field: string,
        value: string,
        shouldValidate?: boolean
    ) => void;
    lookups: WasteMovementReferenceLookups;
    readOnlyForm?: boolean;
    /** Receipt capture (Received only). Required for new/captured records; optional-as-a-whole on legacy records. */
    captureRequired?: boolean;
    /** True when editing a saved record that has no receipt capture yet. */
    legacyWithoutCapture?: boolean;
    carrierLookups?: WasteMovementCarrierLookups;
    /** Applies a multi-field update (haulier/vehicle pre-fill copies several values at once). */
    applyValues?: (updater: (current: WasteMovementFormValues) => WasteMovementFormValues) => void;
    /** S1C: the EWC classification a save is checked against and what the saved record already holds. */
    hazardousPops?: { ewc: EffectiveEwcClassification; stored: StoredHazardousPopsState };
    /** S1D: whether the saved record already has a broker/dealer (removing it needs a confirmation). */
    hasStoredBrokerDealer?: boolean;
};

function fieldErrorMessage(
    field: keyof WasteMovementFormValues,
    errors: FormikErrors<WasteMovementFormValues>,
    touched: FormikTouched<WasteMovementFormValues>,
    hasPersistedRecord: boolean,
    submitCount: number,
    liveValidationAfterSubmit: boolean
) {
    if (
        !shouldShowFormFieldError(
            errors,
            field,
            hasPersistedRecord,
            submitCount,
            liveValidationAfterSubmit,
            touched
        )
    ) {
        return null;
    }
    const message = errors[field];
    if (typeof message !== "string") return null;
    return <div className="invalid-feedback d-block">{message}</div>;
}

export function WasteMovementFormGrid({
    movementDirection,
    isNew,
    sites,
    showSiteField,
    values,
    errors,
    touched,
    submitCount,
    liveValidationAfterSubmit,
    setFieldValue,
    lookups,
    readOnlyForm = false,
    captureRequired = false,
    legacyWithoutCapture = false,
    carrierLookups,
    applyValues,
    hazardousPops,
    hasStoredBrokerDealer = false,
}: Props) {
    const isReceived = movementDirection === "Received";
    const captureMarker = captureRequired ? <RequiredFieldMarker /> : null;
    const municipalOptions = lookups.municipalDegradables.filter((m) => m.indicatorType === "Municipal");
    const degradableOptions = lookups.municipalDegradables.filter((m) => m.indicatorType === "Degradable");
    const hasPersistedRecord = !isNew;
    const showErr = (field: keyof WasteMovementFormValues) =>
        shouldShowFormFieldError(
            errors,
            field,
            hasPersistedRecord,
            submitCount,
            liveValidationAfterSubmit,
            touched
        );
    const shouldValidateOnChange = submitCount > 0 || liveValidationAfterSubmit;
    const updateField = (field: keyof WasteMovementFormValues, value: string) => {
        void setFieldValue(field, value, shouldValidateOnChange);
    };
    const controlClass = (field: keyof WasteMovementFormValues, base = "form-control") =>
        `${base}${showErr(field) ? " is-invalid" : ""}`;
    const selectClass = (field: keyof WasteMovementFormValues) =>
        controlClass(field, "form-select");
    // The receipt time is UK (Europe/London) time. When the clocks go back the entered time happens twice, so the
    // operator has to say which one; the choice only appears for such a time.
    const receiptTimeResolution = isReceived ? resolveLondonInstant(values.movementDate, values.receivedTime) : null;
    const ambiguousReceiptTime = receiptTimeResolution?.kind === "ambiguous" ? receiptTimeResolution : null;
    return (
        <>
            {showSiteField && isNew && (
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Site <RequiredFieldMarker /></Form.Label>
                            <Form.Select
                                value={values.siteId}
                                onChange={(e) => updateField("siteId", e.target.value)}
                                className={selectClass("siteId")}
                                disabled={readOnlyForm}
                                required
                            >
                                <option value="">Select site…</option>
                                {sites.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.siteName}
                                    </option>
                                ))}
                            </Form.Select>
                            {fieldErrorMessage("siteId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                        </Form.Group>
                    </Col>
                </Row>
            )}
            <Row className="mb-3">
                <Col md={isReceived ? 3 : 6}>
                    <Form.Group>
                        <Form.Label>Date <RequiredFieldMarker /></Form.Label>
                        <Form.Control
                            type="date"
                            value={values.movementDate}
                            onChange={(e) => updateField("movementDate", e.target.value)}
                            className={controlClass("movementDate")}
                            disabled={readOnlyForm}
                            required
                        />
                        {fieldErrorMessage("movementDate", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                    </Form.Group>
                </Col>
                {isReceived && (
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label htmlFor="waste-movement-received-time">Time received (UK time) {captureMarker}</Form.Label>
                            <Form.Control
                                id="waste-movement-received-time"
                                type="time"
                                step={60}
                                value={values.receivedTime}
                                onChange={(e) => updateField("receivedTime", e.target.value)}
                                className={controlClass("receivedTime")}
                                disabled={readOnlyForm}
                            />
                            {fieldErrorMessage("receivedTime", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            {ambiguousReceiptTime && (
                                <>
                                    <Form.Select
                                        id="waste-movement-received-time-occurrence"
                                        aria-label="Which occurrence of the time received (the clocks go back on this date)"
                                        value={values.receivedTimeOccurrence}
                                        onChange={(e) => updateField("receivedTimeOccurrence", e.target.value)}
                                        className={`${selectClass("receivedTimeOccurrence")} mt-2`}
                                        disabled={readOnlyForm}
                                    >
                                        <option value="">Clocks go back: which {values.receivedTime}?</option>
                                        <option value="earlier">
                                            First {values.receivedTime} ({ambiguousReceiptTime.earlier.zoneLabel}, before the clocks go back)
                                        </option>
                                        <option value="later">
                                            Second {values.receivedTime} ({ambiguousReceiptTime.later.zoneLabel}, after the clocks go back)
                                        </option>
                                    </Form.Select>
                                    {fieldErrorMessage("receivedTimeOccurrence", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                                </>
                            )}
                        </Form.Group>
                    </Col>
                )}
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Reference</Form.Label>
                        <Form.Control
                            type="text"
                            value={values.reference}
                            onChange={(e) => updateField("reference", e.target.value)}
                            maxLength={128}
                            placeholder="Ticket / slip reference (optional)"
                            disabled={readOnlyForm}
                        />
                    </Form.Group>
                </Col>
            </Row>
            {movementDirection === "Received" ? (
                <Row className="mb-3">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label htmlFor="waste-movement-origin">Origin <RequiredFieldMarker /></Form.Label>
                            <RegLookupSelect
                                inputId="waste-movement-origin"
                                value={values.originGeographyId}
                                onChange={(id) => updateField("originGeographyId", id)}
                                items={lookups.geographies}
                                placeholder="Select origin…"
                                codeOnly
                                disabled={readOnlyForm}
                                invalid={showErr("originGeographyId")}
                            />
                            {fieldErrorMessage("originGeographyId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                        </Form.Group>
                    </Col>
                </Row>
            ) : (
                <Row className="mb-3">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label htmlFor="waste-movement-destination">Destination <RequiredFieldMarker /></Form.Label>
                            <RegLookupSelect
                                inputId="waste-movement-destination"
                                value={values.destinationGeographyId}
                                onChange={(id) => updateField("destinationGeographyId", id)}
                                items={lookups.geographies}
                                placeholder="Select destination…"
                                codeOnly
                                disabled={readOnlyForm}
                                invalid={showErr("destinationGeographyId")}
                            />
                            {fieldErrorMessage("destinationGeographyId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                        </Form.Group>
                    </Col>
                </Row>
            )}
            {movementDirection === "Received" ? (
                <>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-ewc">EWC Code <RequiredFieldMarker /></Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-ewc"
                                    value={values.regEwcCodeId}
                                    onChange={(id) => updateField("regEwcCodeId", id)}
                                    items={lookups.ewcs}
                                    placeholder="Select EWC…"
                                    formatCode={formatEwcDisplayCode}
                                    disabled={readOnlyForm}
                                invalid={showErr("regEwcCodeId")}
                                />
                                {fieldErrorMessage("regEwcCodeId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-disposal-recovery">
                                    Disposal or Recovery Code <RequiredFieldMarker />
                                </Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-disposal-recovery"
                                    value={values.regDisposalRecoveryCodeId}
                                    onChange={(id) => updateField("regDisposalRecoveryCodeId", id)}
                                    items={lookups.disposalRecoveryCodes}
                                    placeholder="Select code…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regDisposalRecoveryCodeId")}
                                />
                                {fieldErrorMessage("regDisposalRecoveryCodeId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Municipal Source <RequiredFieldMarker /></Form.Label>
                                <Form.Select
                                    value={values.municipalFlagId}
                                    onChange={(e) => updateField("municipalFlagId", e.target.value)}
                                    className={selectClass("municipalFlagId")}
                                    disabled={readOnlyForm}
                                    required
                                >
                                    <option value="">Select…</option>
                                    {municipalOptions.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.applies}
                                        </option>
                                    ))}
                                </Form.Select>
                                {fieldErrorMessage("municipalFlagId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Degradable <RequiredFieldMarker /></Form.Label>
                                <Form.Select
                                    value={values.degradableFlagId}
                                    onChange={(e) => updateField("degradableFlagId", e.target.value)}
                                    className={selectClass("degradableFlagId")}
                                    disabled={readOnlyForm}
                                    required
                                >
                                    <option value="">Select…</option>
                                    {degradableOptions.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.applies}
                                        </option>
                                    ))}
                                </Form.Select>
                                {fieldErrorMessage("degradableFlagId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-state">State <RequiredFieldMarker /></Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-state"
                                    value={values.regPhysicalStateId}
                                    onChange={(id) => updateField("regPhysicalStateId", id)}
                                    items={lookups.physicalStates}
                                    placeholder="Select state…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regPhysicalStateId")}
                                />
                                {fieldErrorMessage("regPhysicalStateId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-source-activity">
                                    Source Activity <RequiredFieldMarker />
                                </Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-source-activity"
                                    value={values.regSourceActivityId}
                                    onChange={(id) => updateField("regSourceActivityId", id)}
                                    items={lookups.sourceActivities}
                                    placeholder="Select…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regSourceActivityId")}
                                />
                                {fieldErrorMessage("regSourceActivityId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-pre-treatment">Pre-Treatment <RequiredFieldMarker /></Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-pre-treatment"
                                    value={values.regPreTreatmentId}
                                    onChange={(id) => updateField("regPreTreatmentId", id)}
                                    items={lookups.preTreatments}
                                    placeholder="Select…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regPreTreatmentId")}
                                />
                                {fieldErrorMessage("regPreTreatmentId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            ) : (
                <>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-ewc">EWC Code <RequiredFieldMarker /></Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-ewc"
                                    value={values.regEwcCodeId}
                                    onChange={(id) => updateField("regEwcCodeId", id)}
                                    items={lookups.ewcs}
                                    placeholder="Select EWC…"
                                    formatCode={formatEwcDisplayCode}
                                    disabled={readOnlyForm}
                                invalid={showErr("regEwcCodeId")}
                                />
                                {fieldErrorMessage("regEwcCodeId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Municipal Source <RequiredFieldMarker /></Form.Label>
                                <Form.Select
                                    value={values.municipalFlagId}
                                    onChange={(e) => updateField("municipalFlagId", e.target.value)}
                                    className={selectClass("municipalFlagId")}
                                    disabled={readOnlyForm}
                                    required
                                >
                                    <option value="">Select…</option>
                                    {municipalOptions.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.applies}
                                        </option>
                                    ))}
                                </Form.Select>
                                {fieldErrorMessage("municipalFlagId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-state">State <RequiredFieldMarker /></Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-state"
                                    value={values.regPhysicalStateId}
                                    onChange={(id) => updateField("regPhysicalStateId", id)}
                                    items={lookups.physicalStates}
                                    placeholder="Select state…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regPhysicalStateId")}
                                />
                                {fieldErrorMessage("regPhysicalStateId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label htmlFor="waste-movement-disposal-recovery">
                                    Disposal or Recovery Code <RequiredFieldMarker />
                                </Form.Label>
                                <RegLookupSelect
                                    inputId="waste-movement-disposal-recovery"
                                    value={values.regDisposalRecoveryCodeId}
                                    onChange={(id) => updateField("regDisposalRecoveryCodeId", id)}
                                    items={lookups.disposalRecoveryCodes}
                                    placeholder="Select code…"
                                    disabled={readOnlyForm}
                                invalid={showErr("regDisposalRecoveryCodeId")}
                                />
                                {fieldErrorMessage("regDisposalRecoveryCodeId", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Amount in Tonnes <RequiredFieldMarker /></Form.Label>
                        <Form.Control
                            type="number"
                            step="0.000001"
                            min="0"
                            value={values.weightTonnes}
                            onChange={(e) => updateField("weightTonnes", e.target.value)}
                            className={controlClass("weightTonnes")}
                            disabled={readOnlyForm}
                            required
                        />
                        {fieldErrorMessage("weightTonnes", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                    </Form.Group>
                </Col>
                {isReceived && (
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Weight recorded as {captureMarker}</Form.Label>
                            <Form.Select
                                value={values.weightIsEstimate}
                                onChange={(e) => updateField("weightIsEstimate", e.target.value)}
                                className={selectClass("weightIsEstimate")}
                                disabled={readOnlyForm}
                            >
                                <option value="">Select…</option>
                                <option value="false">Actual (weighed)</option>
                                <option value="true">Estimated</option>
                            </Form.Select>
                            {fieldErrorMessage("weightIsEstimate", errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                        </Form.Group>
                    </Col>
                )}
            </Row>
            {isReceived && (
                <ReceiptDetailsSection
                    values={values}
                    lookups={lookups}
                    readOnlyForm={readOnlyForm}
                    captureMarker={captureMarker}
                    legacyWithoutCapture={legacyWithoutCapture}
                    updateField={updateField}
                    showErr={showErr}
                    selectClass={selectClass}
                    controlClass={controlClass}
                    errorFor={(field) => fieldErrorMessage(field, errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                />
            )}
            {isReceived && (
                <HazardousPopsSections
                    values={values}
                    errors={errors as unknown as HazardousPopsErrors}
                    showErrors={hasPersistedRecord || submitCount > 0 || liveValidationAfterSubmit}
                    readOnlyForm={readOnlyForm}
                    isNew={isNew}
                    lookups={{
                        hazardousPropertyCodes: lookups.hazardousPropertyCodes ?? [],
                        popCodes: lookups.popCodes ?? [],
                    }}
                    ewc={
                        hazardousPops?.ewc ??
                        effectiveEwcClassification({ isNew: true, detail: null, regEwcCodeId: values.regEwcCodeId, ewcs: lookups.ewcs })
                    }
                    stored={hazardousPops?.stored ?? storedHazardousPopsState(null)}
                    requiredMarker={captureMarker}
                    updateField={updateField}
                    applyValues={applyValues}
                />
            )}
            {isReceived && (
                <CarrierSection
                    values={values}
                    carrierLookups={carrierLookups ?? { hauliers: [], vehicles: [] }}
                    readOnlyForm={readOnlyForm}
                    captureMarker={captureMarker}
                    updateField={updateField}
                    applyValues={applyValues}
                    showErr={showErr}
                    selectClass={selectClass}
                    controlClass={controlClass}
                    errorFor={(field) => fieldErrorMessage(field, errors, touched, hasPersistedRecord, submitCount, liveValidationAfterSubmit)}
                />
            )}
            {isReceived && (
                <BrokerDealerSection
                    values={values}
                    errors={errors as unknown as BrokerDealerErrors}
                    showErrors={hasPersistedRecord || submitCount > 0 || liveValidationAfterSubmit}
                    readOnlyForm={readOnlyForm}
                    isNew={isNew}
                    hasStored={hasStoredBrokerDealer}
                    requiredMarker={captureMarker}
                    updateField={updateField}
                    applyValues={applyValues}
                />
            )}
            <Row className="mb-3">
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={values.notes}
                            onChange={(e) => updateField("notes", e.target.value)}
                            disabled={readOnlyForm}
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
}

type SectionProps = {
    values: WasteMovementFormValues;
    readOnlyForm: boolean;
    captureMarker: JSX.Element | null;
    updateField: (field: keyof WasteMovementFormValues, value: string) => void;
    showErr: (field: keyof WasteMovementFormValues) => boolean;
    selectClass: (field: keyof WasteMovementFormValues) => string;
    controlClass: (field: keyof WasteMovementFormValues, base?: string) => string;
    errorFor: (field: keyof WasteMovementFormValues) => JSX.Element | null;
};

function ReceiptDetailsSection({
    values,
    lookups,
    readOnlyForm,
    captureMarker,
    legacyWithoutCapture,
    updateField,
    errorFor,
    selectClass,
    controlClass,
}: SectionProps & { lookups: WasteMovementReferenceLookups; legacyWithoutCapture: boolean }) {
    return (
        <div className="waste-movement-form-panel mb-3">
            <div className="fw-semibold mb-2">Receipt details</div>
            {legacyWithoutCapture && (
                <Alert variant="info" className="py-2 mb-3">
                    Receipt details were not recorded for this movement. Leave them all blank to keep the record as it is, or
                    complete them in full.
                </Alert>
            )}
            <Row className="mb-3">
                <Col md={12}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-description">Waste description {captureMarker}</Form.Label>
                        <Form.Control
                            id="waste-movement-description"
                            as="textarea"
                            rows={2}
                            maxLength={5000}
                            value={values.wasteDescription}
                            onChange={(e) => updateField("wasteDescription", e.target.value)}
                            className={controlClass("wasteDescription")}
                            placeholder="What the load is, e.g. mixed construction and demolition waste"
                            disabled={readOnlyForm}
                        />
                        {errorFor("wasteDescription")}
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={8}>
                    <Form.Group>
                        <Form.Label>Container type {captureMarker}</Form.Label>
                        <Form.Select
                            value={values.containerTypeCode}
                            onChange={(e) => updateField("containerTypeCode", e.target.value)}
                            className={selectClass("containerTypeCode")}
                            disabled={readOnlyForm}
                        >
                            <option value="">Select…</option>
                            {lookups.containerTypes.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.description}
                                </option>
                            ))}
                        </Form.Select>
                        {errorFor("containerTypeCode")}
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Number of containers {captureMarker}</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={values.numberOfContainers}
                            onChange={(e) => updateField("numberOfContainers", e.target.value)}
                            className={controlClass("numberOfContainers")}
                            placeholder="Loose load: 1"
                            disabled={readOnlyForm}
                        />
                        {errorFor("numberOfContainers")}
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Contains hazardous properties? {captureMarker}</Form.Label>
                        <Form.Select
                            value={values.containsHazardous}
                            onChange={(e) => updateField("containsHazardous", e.target.value)}
                            className={selectClass("containsHazardous")}
                            disabled={readOnlyForm}
                        >
                            <option value="">Select…</option>
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </Form.Select>
                        {errorFor("containsHazardous")}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Contains POPs (persistent organic pollutants)? {captureMarker}</Form.Label>
                        <Form.Select
                            value={values.containsPops}
                            onChange={(e) => updateField("containsPops", e.target.value)}
                            className={selectClass("containsPops")}
                            disabled={readOnlyForm}
                        >
                            <option value="">Select…</option>
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </Form.Select>
                        {errorFor("containsPops")}
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
}

function CarrierSection({
    values,
    carrierLookups,
    readOnlyForm,
    captureMarker,
    updateField,
    applyValues,
    showErr,
    errorFor,
    selectClass,
    controlClass,
}: SectionProps & {
    carrierLookups: WasteMovementCarrierLookups;
    applyValues?: (updater: (current: WasteMovementFormValues) => WasteMovementFormValues) => void;
}) {
    const haulierItems: RegLookupSelectItem[] = carrierLookups.hauliers.map((h) => ({
        id: h.id,
        code: h.name,
        description: h.carrierLicenceNumber ?? "",
    }));
    const vehicleItems: RegLookupSelectItem[] = carrierLookups.vehicles.map((v) => ({
        id: v.id,
        code: v.registration,
        description: v.vehicleType ?? "",
    }));
    const isRoad = values.carrierMeansOfTransport === MEANS_OF_TRANSPORT_ROAD;
    const hasRegistration = values.carrierRegistrationNumber.trim() !== "";
    const prefillDisabled = readOnlyForm || !applyValues;

    return (
        <div className="waste-movement-form-panel mb-3">
            <div className="fw-semibold mb-2">Carrier</div>
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-carrier-haulier">Haulier (pre-fill from your list)</Form.Label>
                        <RegLookupSelect
                            inputId="waste-movement-carrier-haulier"
                            value={values.carrierHaulierId}
                            onChange={(id) =>
                                applyValues?.((current) =>
                                    applyHaulierPrefill(current, carrierLookups.hauliers.find((h) => h.id === id) ?? null)
                                )
                            }
                            items={haulierItems}
                            placeholder="Select a haulier to copy their details…"
                            codeOnly
                            isClearable
                            disabled={prefillDisabled}
                        />
                        <div className="form-text">The details below are saved with this movement; later changes to the haulier record will not alter them.</div>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Carrier organisation name {captureMarker}</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={255}
                            value={values.carrierOrganisationName}
                            onChange={(e) => updateField("carrierOrganisationName", e.target.value)}
                            className={controlClass("carrierOrganisationName")}
                            disabled={readOnlyForm}
                        />
                        {errorFor("carrierOrganisationName")}
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Carrier registration number {captureMarker}</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={64}
                            value={values.carrierRegistrationNumber}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (next.trim() !== "" && values.carrierReasonForNoRegistrationNumber !== "" && applyValues) {
                                    applyValues((current) => ({
                                        ...current,
                                        carrierRegistrationNumber: next,
                                        carrierReasonForNoRegistrationNumber: "",
                                    }));
                                    return;
                                }
                                updateField("carrierRegistrationNumber", next);
                            }}
                            className={controlClass("carrierRegistrationNumber")}
                            placeholder="e.g. CBDU123456"
                            disabled={readOnlyForm}
                        />
                        {errorFor("carrierRegistrationNumber")}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Reason for no registration number {!hasRegistration ? captureMarker : null}</Form.Label>
                        <Form.Select
                            value={values.carrierReasonForNoRegistrationNumber}
                            onChange={(e) => updateField("carrierReasonForNoRegistrationNumber", e.target.value)}
                            className={selectClass("carrierReasonForNoRegistrationNumber")}
                            disabled={readOnlyForm || hasRegistration}
                        >
                            <option value="">{hasRegistration ? "Not applicable" : "Select…"}</option>
                            {NO_REGISTRATION_REASON_OPTIONS.map((r) => (
                                <option key={r.code} value={r.code}>
                                    {r.label}
                                </option>
                            ))}
                        </Form.Select>
                        {errorFor("carrierReasonForNoRegistrationNumber")}
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Means of transport {captureMarker}</Form.Label>
                        <Form.Select
                            value={values.carrierMeansOfTransport}
                            onChange={(e) => updateField("carrierMeansOfTransport", e.target.value)}
                            className={selectClass("carrierMeansOfTransport")}
                            disabled={readOnlyForm}
                        >
                            <option value="">Select…</option>
                            {MEANS_OF_TRANSPORT_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </Form.Select>
                        {errorFor("carrierMeansOfTransport")}
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-carrier-vehicle">Vehicle (pre-fill from your list)</Form.Label>
                        <RegLookupSelect
                            inputId="waste-movement-carrier-vehicle"
                            value={values.carrierVehicleId}
                            onChange={(id) =>
                                applyValues?.((current) =>
                                    applyVehiclePrefill(current, carrierLookups.vehicles.find((v) => v.id === id) ?? null)
                                )
                            }
                            items={vehicleItems}
                            placeholder="Select a vehicle…"
                            codeOnly
                            isClearable
                            disabled={prefillDisabled}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Vehicle registration {isRoad ? captureMarker : null}</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={32}
                            value={values.carrierVehicleRegistration}
                            onChange={(e) => updateField("carrierVehicleRegistration", e.target.value)}
                            className={controlClass("carrierVehicleRegistration")}
                            disabled={readOnlyForm}
                        />
                        {errorFor("carrierVehicleRegistration")}
                        {!isRoad && values.carrierMeansOfTransport !== "" && !showErr("carrierVehicleRegistration") && (
                            <div className="form-text">Only needed for road transport.</div>
                        )}
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
}
